import CartPreviewDialog from './cart-preview-dialog.js';
import addToCart from './add-to-cart.js';
import addToCartModalContent from './add-to-cart-modal-content.js';
import getAction from '../../../../Assets/helpers/form/get-action.js';
import getFields from '../../../../Assets/helpers/form/get-fields.js';
import getFormData from '../../../../Assets/helpers/form/get-from-data.js';
import getProductDataFromAttributes from '../../../../Assets/helpers/tracking/get-product-data-from-attributes.js';
import getTargetValue from './get-target-value.js';
import number from '../../../../Assets/helpers/type/number.js';
import parseProductDataFromValue from './parse-product-data-from-value.js';

/** @typedef {import('./typedefs.js').CartProduct} CartProduct */

/**
 * Before add cart item event This event is cancellable
 *
 * @type {CustomEvent}
 * @property {object} detail
 * @property {CartProduct[]} detail.items Products to add to cart
 * @event beforeaddcartitem
 */

/**
 * Cart add item event This event is not cancellable
 *
 * @type {CustomEvent}
 * @property {object} detail
 * @property {CartProduct[]} detail.items Products added to cart
 * @event addcartitem
 */

/**
 * Cart change event This event is not cancellable
 *
 * @type {CustomEvent}
 * @property {object} detail
 * @property {CartProduct[]} detail.items Products in the cart
 * @event cartchange
 */

/**
 * Error add cart item This event is not cancellable
 *
 * @type {CustomEvent}
 * @property {object} detail
 * @property {CartProduct[]} detail.items Products to add to cart
 * @event error
 */

/**
 * Component to add product(s) to cart `data-target` define behavior:
 *
 * - `modal` (default): open a modal with the cart content (or partial content)
 * - `self`: navigate to destination (action)
 * - `inline`: update buttons only ("added to cart") submitter `data-formtarget` override form target (see data-target)
 *
 * @example
 *   ```html
 *     <form class="js-ProductBuy" action="/basket/add" method="post">
 *       <input type="hidden" name="products" value="10337739-19742a70-1efd-8c09-f550-e282e75ad46d" data-prid="10337739" data-offer="19742a70-1efd-8c09-f550-e282e75ad46d">
 *       <input type="checkbox" name="products" value"10337739(5516265)" data-prid="5516265" data-relatedprid="10337739" id="somewarranty"><label for="somewarranty">Some warranty</label>
 *       <button>Add to cart</button>
 *       <label for="quantity">Quantity (1-100):</label><input type="number" name="products[10337739-19742a70-1efd-8c09-f550-e282e75ad46d].Quantity" min="1" max="100" value="1" id="quantity">
 *       <button name="shopid" value="17">Add to cart for pickup at Fnac Paris Forum des Halles</button>
 *       <button name="products" value"10337739(5516265)" data-prid="5516265" data-relatedprid="10337739">Add to cart with warranty</button>
 *       <!-- fields name="products[{id}].Order" are used to order products -->
 *     </form>
 *     <button form="someform" action="/basket/oneclick" data-formtarget="self">Add to cart one click (+ remote)</button>
 *   ```;
 *
 * @fires addcartitem
 * @fires beforeaddcartitem
 * @fires cartchange
 * @fires error
 */
export default class ProductBuyForm {
  constructor({ el }) {
    this.el = el;
  }

  /**
   * @param {SubmitEvent} event
   * @returns {Promise<void>}
   */
  async handleSubmit(event) {
    const { submitter } = event;
    const items = this.#getProducts(submitter);

    const beforeEvent = new CustomEvent('beforeaddcartitem', {
      bubbles: true,
      cancelable: true,
      detail: { items }
    });

    // Is the before event default prevented
    if (!this.el.dispatchEvent(beforeEvent)) {
      event.preventDefault(); // prevent default action of form submit
      return;
    }

    // Get target and action values from submitter or the form itself or default
    const target = getTargetValue(submitter?.dataset.formtarget) ?? getTargetValue(this.el.dataset.target) ?? 'modal';
    // /!\ button.formAction default value is not button.form.action but button.baseURI see https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#dom-fs-formaction
    // /!\ button.action default value is not null but button.baseURI see https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#dom-fs-action
    // This why we need to check the attribute instead of the property formAction itself. Also the attribute not rebase (ie. submitter.getAttribute("formaction)==="/page" && submitter.formAction==="https://example.com/page")
    const action = getAction(this.el, submitter);

    // There are different ways to show to the user how the product has been added to cart:
    try {
      switch (target) {
        case 'inline':
          await this.#submitInline(items, event, action);
          return;
        case 'self':
          await this.#submitSelf(items, event);
          return;
        case 'modal':
        default:
          await this.#submitModal(items, event, action);
          return;
      }
    } catch (error) {
      this.el.dispatchEvent(
        new CustomEvent('error', {
          bubbles: true,
          detail: { items }
        })
      );
      throw error;
    }
  }

  async #submitInline(products, event, action) {
    const { submitter } = event;
    event.preventDefault();

    // Update the button immediately, no need to wait the network
    const {
      className,
      innerHTML,
      style: { pointerEvents }
    } = submitter; // save current state
    // NOTE: buttons (as components) should update themself instead, by listing addcartitem or cartchange events
    // default buy button
    if (submitter.classList.contains('f-button')) {
      submitter.classList.remove('f-button--orange', 'f-button--blue');
      submitter.classList.add('f-button--green');
      submitter.style.pointerEvents = 'none';
    }

    submitter.innerHTML = '<span class="f-icon f-button__icon">✔</span>Dans le panier !';

    try {
      await addToCart(products, action);
      this.#dispatchCartChange(products, submitter);
    } catch (error) {
      // In case of error (like network) restore the previous state
      submitter.className = className;
      submitter.innerHTML = innerHTML;
      submitter.style.pointerEvents = pointerEvents;
      throw error;
    }
  }

  async #submitSelf(products, event) {
    this.#dispatchCartChange(products, event.submitter);
  }

  async #submitModal(products, event, action) {
    event.preventDefault();

    try {
      this.#openDialogWithContent(products, event.submitter);
      await addToCartModalContent(products, action);
      this.#dispatchCartChange(products, event.submitter);
    } catch (e) {
      reportError(e);
    }
  }

  /**
   * @param {CartProduct[]} items
   * @param {Element} relatedTarget The element that initiate the cart change
   */
  #dispatchCartChange(items, relatedTarget = null) {
    this.el.dispatchEvent(
      new CustomEvent('addcartitem', {
        bubbles: true,
        detail: { items, relatedTarget }
      })
    );

    // Let's everyone know cart change
    globalThis.dispatchEvent(
      new CustomEvent('cartchange', {
        detail: {
          items: [] /* NOTE: list the content of the cart await getCart().items */,
          relatedTarget
        }
      })
    );
  }

  /**
   * Build and open the pop-in basket.
   *
   * @param {object} data - Cart data from API
   * @param {CartProduct[]} products - Products added to the cart
   * @param {Element} relatedTarget - The element that initiated the cart change
   */
  #openDialogWithContent(products, relatedTarget) {
    // NOTE: this should be auto instantiable component
    const modal = new CartPreviewDialog({ products, relatedTarget });

    modal.showModal();
  }

  /**
   * Get products object from the form and optional submitter Used for dispatched events/datalayer or some custom
   * actions
   *
   * @param {HTMLButtonElement | HTMLInputElement} submitter
   * @returns {CartProduct[]}
   */
  #getProducts(submitter = null) {
    const formData = getFormData(this.el, submitter);
    // should be named "storeid", but CrossSellBasketController name it differently (and consequently CommunityController StoreLocatorWithAvailability)
    // same store for all products
    const store = formData.get('shopid');

    return getFields(this.el, submitter)
      .filter((el) => el.name === 'products')
      .map((element) => ({
        order: number(Number.parseInt(formData.get(`products[${element.value}].Order`), 10), Number.POSITIVE_INFINITY),
        element
      }))
      .sort((a, b) => a.order - b.order)
      .map(({ element }) => ({
        store,
        quantity: 1, // assuming single product for now
        ...getProductDataFromAttributes(element),
        relatedPrid: element.dataset.relatedprid,

        // Parse product data from field value until the (mobile) controller CommunityController.StoreLocatorWithAvailability() provide a view model with data to render products attributes to inputs. See _StoreLocatorArticleAvailability.cshtml
        ...parseProductDataFromValue(element.value)
      }));
  }
}

// Event delegation: single listener for all forms (including those loaded async)
// Guard against multiple registrations
if (!globalThis.__productBuyForm) {
  globalThis.__productBuyForm = true;

  document.addEventListener('submit', async (event) => {
    if (event.target.matches('.js-ProductBuy')) {
      const buyForm = new ProductBuyForm({ el: event.target });
      await buyForm.handleSubmit(event);
    }
  });
}
